import { Form, useFormContext } from '@calendly/form';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { useEditorContent } from '@calendly/rich-text-editor';
import { BrandLogo } from '@calendly/ui/components/brand-logo';
import { Button } from '@calendly/ui/components/button';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { AlertWarningTriangleIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { Popup } from '@calendly/ui/components/popup';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { capitalize } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetSendOptionsQuery } from 'common';
import { CRM_SHAREABLE, CRM_SHARE_AVAIL, CRM_CONTACT_CARD, CRM_CONTACT_PAGE, CRM_INTERACTIONS_FEED } from 'features/mfe_shareable';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants';
import { useFFTrackingV2 } from 'hooks/analytics_events/use_ff_tracking_v2';
import { useTrackOnce, useTrack } from 'hooks/analytics_events/useTrack';
import { backUpValuesSelector, withCloseConfirmationSelector, INVITEE_EXPERIENCE_SEND_FROM_CALENDLY_Q3 } from '../selectors';
import { showConfirmation } from '../slice';
import { excludeTempFormValues } from '../utils';
import { BccField } from './bcc_field';
import { BodyField } from './body_field';
import { CcField } from './cc_field';
import { Errors } from './errors';
import { EventCancellationBanner } from './event_cancellation_banner';
import { Footer } from './footer';
import { getValidationSchema } from './get_validation_schema';
import { Header } from './popup_header';
import { ReminderField } from './reminder_field';
import { SendFromField } from './send_from_field';
import { SubjectField } from './subject_field';
import { ToField } from './to_field';
const formBodyIsPhoneCss = "fveq1nl";
const formBodyCss = "fxj3gnz";
const FormBody = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(formBodyCss, {
    [formBodyIsPhoneCss]: isPhone
  }, props.className)} />;
});
const sendFromContainerCss = "s1k8n9zs";
const SendFromContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(sendFromContainerCss, props.className)} />;
});
const fieldsContainerIsPhoneCss = "f174b08w";
const fieldsContainerCss = "f1yz65si";
const FieldsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(fieldsContainerCss, {
    [fieldsContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const connectEmailContainerCss = "c1369r8s";
const ConnectEmailContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(connectEmailContainerCss, props.className)} />;
});
const logoWrapperCss = "l1tue9jh";
const LogoWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(logoWrapperCss, props.className)} />;
});
const buttonWrapperCss = "bwml4wb";
const ButtonWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonWrapperCss, props.className)} />;
});
const FormValuesBackup = ({
  onFormValuesChange
}) => {
  const {
    values
  } = useFormContext();
  useEffect(() => {
    onFormValuesChange(values);
  }, [values, onFormValuesChange]);
  return null;
};
export const SendPopup = ({
  initialFormValues,
  onSend,
  onCancel,
  onBack = null,
  viewConfig = {},
  source = null,
  trackingSource = null
}) => {
  const [isEditorReady, {
    fromHTML: htmlToEditorContent
  }] = useEditorContent();
  const dispatch = useDispatch();
  const withConfirmation = useSelector(withCloseConfirmationSelector);
  const backUpValues = useSelector(backUpValuesSelector);
  const [tmpValues, setTmpValues] = useState({});
  const {
    data,
    isLoading,
    isSuccess
  } = useGetSendOptionsQuery({
    ownerType: 'users',
    ownerId: 'me'
  }, {
    refetchOnFocus: true
  });
  const sendOptions = useMemo(() => isSuccess ? data.sendOptions : [], [isSuccess, data]);
  const [sendWith, setSendWith] = useState(sendOptions.length ? sendOptions[0].type : null);
  const accessNotGrantedForChosenOption = sendOptions.some(option => option.accessGranted === false && option.type === sendWith);
  const showBanner = Boolean(!sendOptions.length && !isLoading || accessNotGrantedForChosenOption);
  useFFTrackingV2(INVITEE_EXPERIENCE_SEND_FROM_CALENDLY_Q3);
  const track = useTrack();
  const {
    isSubjectAtTop = false,
    isReminderFieldVisible = true,
    showConnectEmailContainer = false,
    showShareSubHeader = true
  } = viewConfig;

  // Use trackingSource for analytics, fall back to source if not provided
  const analyticsSource = trackingSource || source;
  useTrackOnce(showBanner && analyticsSource === CRM_SHAREABLE, {
    name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
    properties: {
      location: 'contacts_page_right_rail',
      action: 'opening',
      modal_name: 'send_email_connect_email'
    }
  });
  useTrackOnce(showBanner && analyticsSource === CRM_CONTACT_CARD, {
    name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
    properties: {
      location: 'contact_hover_card',
      action: 'opening',
      modal_name: 'send_email_connect_email'
    }
  });
  useTrackOnce(showBanner && analyticsSource === CRM_CONTACT_PAGE, {
    name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
    properties: {
      location: 'contact_details_page_header',
      action: 'opening',
      modal_name: 'send_email_connect_email'
    }
  });
  useTrackOnce(showBanner && analyticsSource === CRM_INTERACTIONS_FEED, {
    name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
    properties: {
      location: 'interactions_feed_emails',
      action: 'opening',
      modal_name: 'send_email_connect_email'
    }
  });
  const LinkComponent = useCallback(({
    children
  }) => <Link href={`/integrations/${sendWith}`} target="_blank" rel="opener" variant="subtle">
      {children}
    </Link>, [sendWith]);
  if (!isEditorReady) return null;
  const handleOnKeyDown = event => {
    // Prevent Enter key from submitting form for CRM_SHAREABLE, CRM_CONTACT_CARD
    if (event.key === 'Enter' && (source === CRM_SHAREABLE || source === CRM_CONTACT_CARD)) {
      event.preventDefault();
    }
  };
  const onSubmit = values => {
    const payloadFormValues = excludeTempFormValues(values);
    if (analyticsSource === CRM_SHAREABLE) {
      track({
        name: TRACKING_EVENT_NAMES.SEND_VIA_CALENDLY,
        properties: {
          contact_source: 'contacts_page_right_rail',
          step: 'send_email'
        }
      });
    }
    if (trackingSource === CRM_CONTACT_CARD && source === CRM_SHAREABLE) {
      track({
        name: TRACKING_EVENT_NAMES.SEND_VIA_CALENDLY,
        properties: {
          contact_source: 'contact_hover_card',
          step: 'send_email'
        }
      });
    }
    if (analyticsSource === CRM_CONTACT_PAGE) {
      track({
        name: TRACKING_EVENT_NAMES.SEND_VIA_CALENDLY,
        properties: {
          contact_source: 'contact_details_page_header',
          step: 'send_email'
        }
      });
    }
    if (analyticsSource === CRM_INTERACTIONS_FEED) {
      track({
        name: TRACKING_EVENT_NAMES.SEND_VIA_CALENDLY,
        properties: {
          contact_source: 'interactions_feed_emails',
          step: 'send_email'
        }
      });
    }
    return onSend({
      ...payloadFormValues,
      body: values.body.html
    });
  };
  const onCloseModal = () => {
    if (analyticsSource === CRM_SHAREABLE || analyticsSource === CRM_SHARE_AVAIL) {
      track({
        name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
        properties: {
          action: 'closing',
          modal_name: 'send_email_connect_email',
          location: 'contacts_page_right_rail'
        }
      });
    }
    if (analyticsSource === CRM_CONTACT_CARD) {
      track({
        name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
        properties: {
          action: 'closing',
          modal_name: 'send_email_connect_email',
          location: 'contact_hover_card'
        }
      });
    }
    if (analyticsSource === CRM_CONTACT_PAGE) {
      track({
        name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
        properties: {
          action: 'closing',
          modal_name: 'send_email_connect_email',
          location: 'contact_details_page_header'
        }
      });
    }
    if (analyticsSource === CRM_INTERACTIONS_FEED) {
      track({
        name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
        properties: {
          action: 'closing',
          modal_name: 'send_email_connect_email',
          location: 'interactions_feed_emails'
        }
      });
    }
    if (withConfirmation) {
      dispatch(showConfirmation(tmpValues));
    } else {
      onCancel();
    }
  };
  const trackConnectToEmailProvider = provider => {
    const destinationMap = {
      gmail: 'gmail_integrations_page',
      outlook: 'outlook_integrations_page'
    };
    track({
      name: 'CTA Clicked',
      properties: {
        cta_key: 'send_from_calendly_no_email_connection',
        cta_text: `connect_to_${provider}`,
        cta_destination: destinationMap[provider]
      }
    });
  };
  const initialValues = {
    ...initialFormValues,
    body: htmlToEditorContent(initialFormValues.body),
    ...backUpValues
  };
  const hasGmailConnected = sendOptions?.some(email => email.type === 'gmail');
  const hasOutlookConnected = sendOptions?.some(email => email.type === 'outlook');
  const shouldShowSendFromContainer = (source === CRM_SHAREABLE || source === CRM_SHARE_AVAIL) && (hasGmailConnected || hasOutlookConnected) || source !== CRM_SHAREABLE && source !== CRM_SHARE_AVAIL;
  const shouldDisableSendButton = !hasGmailConnected && !hasOutlookConnected;
  return <Popup isOpen onClose={onCloseModal} size="medium" withCloseButton>
      <Header source={source} />
      <Form initialValues={initialValues} onSubmit={onSubmit} validationSchema={getValidationSchema(source)} validateOnChange>
        <FormValuesBackup onFormValuesChange={setTmpValues} />
        <FormBody>
          {showBanner && showConnectEmailContainer && <ConnectEmailContainer>
              <TitleText level={3}>
                {I18n.t('publisher.send_from_calendly.send_with_dropdown.unselected_state')}
              </TitleText>
              <BodyText>
                {I18n.t('publisher.send_from_calendly.send_with_dropdown.send_emails_helper_text')}
              </BodyText>
              <ButtonWrapper>
                <Button as="a" decoration="secondary-outline" href="/integrations/gmail" target="_blank" rel="noreferrer" key="connect_gmail" onClick={() => trackConnectToEmailProvider('gmail')}>
                  <LogoWrapper>
                    <BrandLogo name="gmail" title="gmail" />
                  </LogoWrapper>
                  {I18n.t('publisher.send_from_calendly.send_with_dropdown.connect_to_gmail')}
                </Button>
                <Button as="a" decoration="secondary-outline" href="/integrations/outlook" target="_blank" rel="noreferrer" key="connect_outlook" onClick={() => trackConnectToEmailProvider('outlook')}>
                  <LogoWrapper>
                    <BrandLogo name="microsoft-outlook" title="microsoft-outlook" />
                  </LogoWrapper>
                  {I18n.t('publisher.send_from_calendly.send_with_dropdown.connect_to_outlook')}
                </Button>
              </ButtonWrapper>
            </ConnectEmailContainer>}
          {shouldShowSendFromContainer && <SendFromContainer>
              {showShareSubHeader && <BodyText weight="bold">
                  {I18n.t('publisher.send_from_calendly.field_caption.send_with')}
                </BodyText>}
              <SendFromField sendOptions={sendOptions} isLoading={isLoading} setSendWith={setSendWith} />
            </SendFromContainer>}
          {showBanner && source !== CRM_SHAREABLE && source !== CRM_SHARE_AVAIL && <ContextualBanner icon={AlertWarningTriangleIcon} variant="danger" spacingTop={12}>
              <BodyText data-testid="email-error-message">
                {sendWith ? HtmlParser(I18n.t('publisher.send_from_calendly.errors.email_provider_not_connected', {
              email_provider: capitalize(sendWith)
            }), {
              LinkComponent
            }) : I18n.t('publisher.send_from_calendly.errors.email_not_connected')}
              </BodyText>
            </ContextualBanner>}
          <FieldsContainer onKeyDown={handleOnKeyDown}>
            {isSubjectAtTop && <SubjectField />}
            <ToField />
            <CcField />
            <BccField />
            {!isSubjectAtTop && <SubjectField />}
            <BodyField hideToolbar={source === CRM_SHAREABLE} hideSuggestTimes={source === CRM_SHAREABLE} />
          </FieldsContainer>
          <EventCancellationBanner />
          {isReminderFieldVisible && <ReminderField />}
          <Errors />
        </FormBody>
        <Footer onBack={onBack} onCancel={onCloseModal} shouldDisableSendButton={shouldDisableSendButton} />
      </Form>
    </Popup>;
};

require("./send_popup.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./send_popup.js");